import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { api, type Gallery, type Client, type Booking } from "../lib/api";
import { navigate } from "../lib/route";
import { slugify, cleanSlugInput } from "../lib/slug";

function hashStr(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  return Math.abs(h);
}
function gradientFor(title: string): string {
  const h = hashStr(title || "untitled");
  const a = h % 360;
  const b = (a + 40 + (h % 60)) % 360;
  return `linear-gradient(135deg, hsl(${a} 65% 55%), hsl(${b} 70% 45%))`;
}
function initialsFor(title: string): string {
  const parts = (title || "?").trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p.charAt(0).toUpperCase()).join("") || "?";
}

type SlugStatus = "idle" | "checking" | "available" | "taken";

export function GalleriesPage() {
  const { t } = useTranslation();
  const [galleries, setGalleries] = useState<Gallery[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [subdomainHost, setSubdomainHost] = useState<string>('');
  const [portfolioSlug, setPortfolioSlug] = useState<string>('');
  const [title, setTitle] = useState("");
  const [slug, setSlug] = useState("");
  const [slugEdited, setSlugEdited] = useState(false);
  const [slugStatus, setSlugStatus] = useState<SlugStatus>("idle");
  const [clientID, setClientID] = useState("");
  const [bookingID, setBookingID] = useState("");
  const [allowDownloads, setAllowDownloads] = useState(true);
  const [isPublic, setIsPublic] = useState(false);

  const slugDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const [list, cls, bks] = await Promise.all([
        api.listGalleries(),
        api.listClients(),
        api.listBookings(),
      ]);
      setGalleries(list);
      setClients(cls);
      setBookings(bks);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
    api.runtimeConfig().then((c) => setSubdomainHost(c.subdomain_host ?? '')).catch(() => {});
    api.getMyPortfolio().then((p) => setPortfolioSlug(p.Slug)).catch(() => {});
  }, []);

  function triggerSlugCheck(value: string) {
    if (slugDebounceRef.current) clearTimeout(slugDebounceRef.current);
    if (!value) {
      setSlugStatus("idle");
      return;
    }
    setSlugStatus("checking");
    slugDebounceRef.current = setTimeout(() => {
      void (async () => {
        try {
          const available = await api.checkGallerySlugAvailable(value);
          setSlugStatus(available ? "available" : "taken");
        } catch {
          setSlugStatus("idle");
        }
      })();
    }, 500);
  }

  function handleSlugChange(raw: string) {
    setSlugEdited(true);
    const cleaned = cleanSlugInput(raw);
    setSlug(cleaned);
    triggerSlugCheck(cleaned);
  }

  function handleSlugBlur(raw: string) {
    const finalized = slugify(raw);
    setSlug(finalized);
    triggerSlugCheck(finalized);
  }

  function handleTitleChange(value: string) {
    setTitle(value);
    if (!slugEdited) {
      const auto = slugify(value);
      setSlug(auto);
      triggerSlugCheck(auto);
    }
  }

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    if (!title.trim()) return;
    const finalSlug = slugify(slug.trim() || title);
    setCreating(true);
    setError(null);
    try {
      const g = await api.createGallery({
        Title: title.trim(),
        Slug: finalSlug,
        ClientID: clientID || undefined,
        BookingID: bookingID || undefined,
        IsPublic: isPublic,
        AllowDownloads: allowDownloads,
      });
      setTitle("");
      setSlug("");
      setSlugEdited(false);
      setSlugStatus("idle");
      setClientID("");
      setBookingID("");
      setShowForm(false);
      navigate(`/galleries/${g.Slug}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create");
    } finally {
      setCreating(false);
    }
  }

  const clientMap = Object.fromEntries(clients.map((c) => [c.ID, c.DisplayName]));

  const galleryShareUrl = (s: string) =>
    subdomainHost && portfolioSlug
      ? 'https://' + portfolioSlug + '.' + subdomainHost + '/share/g/' + s
      : window.location.origin + '/share/g/' + s;

  const galleryShareDisplay = (s: string) =>
    galleryShareUrl(s).replace(/^https?:\/\//, '');

  function SlugIndicator() {
    if (slugStatus === "idle") return null;
    if (slugStatus === "checking") {
      return (
        <small style={{ color: "var(--muted)", display: "flex", alignItems: "center", gap: "0.25rem", marginTop: "0.2rem" }}>
          <span style={{ display: "inline-block", width: "0.7em", height: "0.7em", border: "2px solid var(--muted)", borderTopColor: "transparent", borderRadius: "50%", animation: "spin 0.6s linear infinite" }} />
          Beschikbaarheid controleren…
        </small>
      );
    }
    if (slugStatus === "available") {
      return (
        <small style={{ color: "var(--success, #16a34a)", display: "flex", alignItems: "center", gap: "0.25rem", marginTop: "0.2rem" }}>
          ✓ beschikbaar
        </small>
      );
    }
    // taken
    return (
      <small style={{ color: "var(--danger, #dc2626)", display: "flex", alignItems: "center", gap: "0.25rem", marginTop: "0.2rem" }}>
        ✗ al in gebruik
      </small>
    );
  }

  return (
    <div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1rem", flexWrap: "wrap", gap: "0.5rem" }}>
        <h1 style={{ margin: 0 }}>{t("galleries.title")}</h1>
        <button className="btn-primary" onClick={() => setShowForm((v) => !v)}>
          {showForm ? "Cancel" : t("galleries.new")}
        </button>
      </div>

      {showForm && (
        <form onSubmit={handleCreate} className="card" style={{ marginBottom: "1.5rem" }}>
          <h3 style={{ marginTop: 0 }}>{t("galleries.new_form_title")}</h3>

          {/* Row 1: Booking selector — full width */}
          <div className="form-group" style={{ marginBottom: "1rem" }}>
            <label>Voor welke boeking? (optioneel)</label>
            <select
              value={bookingID}
              onChange={(e) => {
                const bId = e.target.value;
                setBookingID(bId);
                if (bId) {
                  const b = bookings.find((bk) => bk.ID === bId);
                  if (b) {
                    // Always auto-fill title and slug from booking
                    setTitle(b.Title);
                    const autoSlug = slugify(b.Title);
                    setSlug(autoSlug);
                    setSlugEdited(false);
                    triggerSlugCheck(autoSlug);
                    if (b.ClientID) setClientID(b.ClientID);
                  }
                } else {
                  // Cleared booking — reset auto-filled fields
                  setTitle("");
                  setSlug("");
                  setSlugEdited(false);
                  setSlugStatus("idle");
                  setClientID("");
                }
              }}
            >
              <option value="">{t("galleries.none_option")}</option>
              {bookings.map((b) => (
                <option key={b.ID} value={b.ID}>{b.Title}</option>
              ))}
            </select>
          </div>

          {/* Row 2: Titel (left) | URL-slug (right) */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "1rem", marginBottom: "1rem" }}>
            <div className="form-group">
              <label>{t("galleries.title_label")}</label>
              <input
                required
                value={title}
                onChange={(e) => handleTitleChange(e.target.value)}
                placeholder="Amsterdam Wedding — June 2026"
              />
            </div>
            <div className="form-group">
              <label>{t("galleries.slug_label")}</label>
              <input
                value={slug}
                onChange={(e) => handleSlugChange(e.target.value)}
                onBlur={(e) => handleSlugBlur(e.target.value)}
                placeholder="amsterdam-wedding-2026"
              />
              <SlugIndicator />
              {slugStatus === "idle" && (
                <small style={{ color: "var(--muted)" }}>
                  Share link: {galleryShareDisplay(slug || "slug")}
                </small>
              )}
            </div>
          </div>

          {/* Row 3: Klant (left half) */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "1rem", marginBottom: "1rem" }}>
            <div className="form-group">
              <label>{t("galleries.client_label")}</label>
              <select value={clientID} onChange={(e) => setClientID(e.target.value)}>
                <option value="">{t("galleries.none_option")}</option>
                {clients.map((c) => (
                  <option key={c.ID} value={c.ID}>{c.DisplayName}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Row 4: Checkboxes */}
          <div style={{ display: "flex", gap: "1rem", flexWrap: "wrap", marginBottom: "1rem" }}>
            <label style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
              <input
                type="checkbox"
                checked={isPublic}
                onChange={(e) => setIsPublic(e.target.checked)}
                style={{ width: "auto" }}
              />
              Public (share page renders)
            </label>
            <label style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
              <input
                type="checkbox"
                checked={allowDownloads}
                onChange={(e) => setAllowDownloads(e.target.checked)}
                style={{ width: "auto" }}
              />
              Allow downloads
            </label>
          </div>

          {/* Row 5: Submit */}
          <button
            type="submit"
            className="btn-primary"
            disabled={creating || slugStatus === "taken" || slugStatus === "checking"}
          >
            {creating ? "Creating…" : "Create gallery"}
          </button>
        </form>
      )}

      {error && <div className="alert alert-error">{error}</div>}

      {loading ? (
        <p style={{ color: "var(--muted)" }}>Loading…</p>
      ) : galleries.length === 0 ? (
        <div className="card">
          <p style={{ margin: 0, color: "var(--muted)" }}>
            {t("galleries.empty")}
          </p>
        </div>
      ) : (
        <div style={{ display: "grid", gap: "1rem", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))" }}>
          {galleries.map((g) => (
            <div
              key={g.ID}
              role="button"
              tabIndex={0}
              onClick={() => navigate(`/galleries/${g.Slug}`)}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") navigate(`/galleries/${g.Slug}`); }}
              className="card"
              style={{ textDecoration: "none", color: "inherit", display: "flex", flexDirection: "column", padding: 0, overflow: "hidden", cursor: "pointer" }}
            >
              <div style={{
                position: "relative",
                aspectRatio: "4 / 3",
                background: g.CoverURL ? `center/cover no-repeat url("${g.CoverURL}")` : gradientFor(g.Title),
              }}>
                {!g.CoverURL && (
                  <div style={{
                    position: "absolute", inset: 0,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    color: "rgba(255,255,255,.9)", fontSize: "2.25rem", fontWeight: 700,
                    letterSpacing: "-0.02em", textShadow: "0 1px 8px rgba(0,0,0,.25)",
                  }}>
                    {initialsFor(g.Title)}
                  </div>
                )}
                <div style={{
                  position: "absolute", top: ".5rem", right: ".5rem",
                  background: "rgba(0,0,0,.55)", color: "#fff",
                  fontSize: ".75rem", padding: ".15rem .5rem", borderRadius: "999px",
                }}>
                  {g.AssetCount} photo{g.AssetCount === 1 ? "" : "s"}
                </div>
              </div>
              <div style={{ padding: "0.75rem 1rem 1rem" }}>
                <strong style={{ display: "block", lineHeight: 1.25 }}>{g.Title}</strong>
                {g.ClientID && clientMap[g.ClientID] && (
                  <small style={{ color: "var(--accent)" }}>{clientMap[g.ClientID]}</small>
                )}
                <small style={{ display: "block", color: "var(--muted)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  <a
                    href={galleryShareUrl(g.Slug)}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: "var(--muted)", textDecoration: "none" }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {galleryShareDisplay(g.Slug)}
                  </a>
                </small>
                <div style={{ marginTop: "0.5rem", display: "flex", gap: "0.4rem", flexWrap: "wrap" }}>
                  {g.IsPublic && <span className="badge badge-info">Public</span>}
                  {g.AllowDownloads && <span className="badge badge-info">Downloads</span>}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
